import dayjs from 'dayjs';
import { TweenMax, TimelineMax, Circ, Power3 } from 'gsap';

class Filters {
  constructor(args) {
    const { containerSelector, itemSelector, perPage, tagSelector } = args;

    this.tagSelector = tagSelector;
    this.containerSelector = containerSelector;

    this.filterToggles = document.querySelectorAll('.js-filter-item');
    this.filterItems = [...document.querySelectorAll(itemSelector)];

    this.clearFiltersButton = document.querySelector('.js-clear-filters');

    this.statusToggles = document.querySelectorAll('.js-status-item');
    this.sortToggles = document.querySelectorAll('.js-sort-item');

    this.noResultsMessage = document.querySelector('.js--filter-no-results');

    this.filterMenuToggle = document.querySelector('.js--filter-menu-toggle');
    this.filterMenuTarget = document.querySelector('.js--filter-menu-target');
    this.filterList = document.querySelector('.filter-list');
    this.timeline = new TimelineMax();
    this.isAnimating = false;

    this.chosenStatus = '';
    this.chosenSort = 'name';
    this.chosenFilters = [];
    this.searchQuery = '';
    this.itemTags = [];

    this.allItems = [];

    this.perPage = perPage;
    this.pageCurrent = 1;
    this.pageStep = 2;
    this.pageCode = '';

    this.initialize();
  }

  initialize() {
    // Tag Filtering
    this.filterToggles.forEach(filterItem => {
      filterItem.addEventListener('click', this.toggleFilter.bind(this));
    });

    if (this.clearFiltersButton) {
      this.clearFiltersButton.addEventListener('click', this.clearFilters.bind(this));
    }

    // Status Filtering
    this.statusToggles.forEach(statusItem => {
      statusItem.addEventListener('click', this.toggleStatus.bind(this));
    });

    // Sorting
    this.sortToggles.forEach(sortItem => {
      sortItem.addEventListener('click', this.toggleSorting.bind(this));
    });

    if (this.filterMenuToggle) {
      // Toggle Mobile Filter Menu
      this.filterMenuToggle.addEventListener('click', this.toggleMobileMenu.bind(this));
    }

    this.allItems = this.filterItems.map(filterItem => {
      const tagObjects = [...filterItem.querySelectorAll(this.tagSelector)];

      const tags = tagObjects.map(tag => tag.innerHTML.trim());

      const newItem = {
        html: filterItem,
        tags: tags,
        date: filterItem.getAttribute('data-js-date'),
        signature: filterItem.getAttribute('data-js-signature'),
        visible: true,
      };

      return newItem;
    });

    this.pageCount = Math.ceil(this.allItems.length / this.perPage);

    this.toggleVisible();
  }

  toggleMobileMenu() {
    const content = this.filterMenuTarget;

    if (!this.isAnimating) {
      content.classList.toggle('filter-list--is-open');
      this.isAnimating = true;
      if (this.filterMenuTarget.classList.contains('filter-list--is-open')) {
        this.filterMenuToggle.innerHTML = 'Hide Filters';
        TweenMax.set(content, { height: 'auto' });
        this.timeline.from(content, 0.3, {
          height: 0,
          ease: Circ.easeOut,
          onComplete: () => {
            this.isAnimating = false;
          },
        });
        this.timeline.to(
          this.filterList,
          0.3,
          {
            autoAlpha: 1,
            ease: Power3.easeInOut,
          },
          '-=0.1',
        );
      } else {
        this.filterMenuToggle.innerHTML = 'Show Filters';
        this.timeline.to(this.filterList, 0.3, {
          autoAlpha: 0,
          ease: Power3.easeInOut,
        });
        this.timeline.to(
          content,
          0.3,
          {
            height: 0,
            ease: Circ.easeOut,
            onComplete: () => {
              this.isAnimating = false;
            },
          },
          '-=0.1',
        );
      }
    }
  }

  toggleStatus(e) {
    const statusName = e.currentTarget.getAttribute('data-js-name');

    this.pageCurrent = 1;
    this.statusToggles.forEach(statusItem =>
      statusItem.classList.remove('filter-list__item--active'),
    );

    if (this.chosenStatus === statusName) {
      this.chosenStatus = '';
      if (this.clearFiltersButton.classList.contains('event-filters__button')) {
        this.clearFiltersButton.classList.add('filter-list__item--active');
      }
    } else {
      this.chosenStatus = statusName;
      e.currentTarget.classList.add('filter-list__item--active');
      if (this.clearFiltersButton.classList.contains('event-filters__button')) {
        this.clearFiltersButton.classList.remove('filter-list__item--active');
      }
    }

    e.currentTarget.blur();
    this.toggleVisible();
  }

  toggleFilter(e) {
    document.querySelector(this.containerSelector).scrollIntoView({ behavior: 'smooth' });

    this.pageCurrent = 1;
    const filterName = e.currentTarget.getAttribute('data-js-name');

    if (this.chosenFilters.includes(filterName)) {
      this.chosenFilters = this.chosenFilters.filter(filter => filter !== filterName);
      e.currentTarget.classList.remove('filter-list__item--active');
    } else {
      this.chosenFilters.push(filterName);
      this.chosenFilters.sort();
      e.currentTarget.classList.add('filter-list__item--active');
    }

    e.currentTarget.blur();
    this.toggleVisible();
  }

  toggleSorting(e) {
    this.pageCurrent = 1;
    const sortingName = e.currentTarget.getAttribute('data-js-name');
    this.chosenSort = sortingName;

    this.sortToggles.forEach(sortItem => sortItem.classList.remove('filter-list__item--active'));
    e.currentTarget.classList.add('filter-list__item--active');
    this.resortItems();
    this.toggleVisible();
  }

  clearFilters() {
    this.chosenFilters = [];
    this.chosenStatus = '';
    this.statusToggles.forEach(toggle => toggle.classList.remove('filter-list__item--active'));
    this.filterToggles.forEach(toggle => toggle.classList.remove('filter-list__item--active'));
    this.pageCurrent = 1;

    if (this.clearFiltersButton.classList.contains('event-filters__button')) {
      this.clearFiltersButton.classList.add('filter-list__item--active');
    }

    this.toggleVisible();
  }

  resortItems() {
    const currentSort = this.chosenSort;

    let itemsArr = [];

    this.allItems.forEach(filterItem => {
      itemsArr.push(filterItem);
    });

    itemsArr = itemsArr.sort(function(a, b) {
      let aname = a.html.querySelector('.accordion__toggle').innerHTML;
      aname = aname.trim().toLowerCase();

      let bname = b.html.querySelector('.accordion__toggle').innerHTML;
      bname = bname.trim().toLowerCase();

      if (aname < bname) {
        return -1;
      }
      if (aname > bname) {
        return 1;
      }
      return 0;
    });

    if (currentSort !== 'name') {
      itemsArr = itemsArr.reverse();
    }
    this.allItems = itemsArr;

    const list = this.filterItems[0].parentNode;

    list.innerHTML = '';

    for (let i = 0; i < itemsArr.length; ++i) {
      list.appendChild(itemsArr[i].html);
    }
  }

  toggleVisible() {
    document.querySelector(this.containerSelector).scrollIntoView({ behavior: 'smooth' });

    this.allItems.forEach(filterItem => {
      filterItem.html.classList.remove('hidden');

      let visible = false;

      if (!this.chosenFilters.length) {
        visible = true;
      } else {
        filterItem.tags.forEach(tag => {
          if (this.chosenFilters.includes(tag.trim())) {
            visible = true;
          }
        });
      }

      if (this.chosenStatus.trim() !== '') {
        const status = filterItem.html.getAttribute('data-js-status');
        if (status !== this.chosenStatus) {
          visible = false;
        }

        if (this.chosenStatus.includes('This Week')) {
          const itemDate = dayjs(filterItem.date);

          if (itemDate.isBefore(dayjs().add(1, 'week'))) {
            visible = true;
          }
        }

        if (this.chosenStatus.includes('KSA Programming')) {
          if (filterItem.signature && filterItem.signature === 'true') {
            visible = true;
          }
        }

        if (filterItem.tags.includes(this.chosenStatus)) {
          visible = true;
        }
      }

      if (!visible) {
        filterItem.visible = false;
        filterItem.html.classList.add('hidden');
      } else {
        filterItem.visible = true;
        filterItem.html.classList.remove('hidden');
      }
    });

    const visibleItems = this.allItems.filter(filterItem => filterItem.visible);

    this.pageCount = Math.ceil(visibleItems.length / this.perPage);

    const paginate = document.querySelector('.js--paginate');

    if (visibleItems.length) {
      this.noResultsMessage.classList.add('visually-hidden');
      paginate.classList.remove('visually-hidden');
    } else {
      this.noResultsMessage.classList.remove('visually-hidden');
      paginate.classList.add('visually-hidden');
    }
    if (this.pageCount === 1) {
      paginate.classList.add('visually-hidden');
    }

    this.startPagination();
    this.setItemsPage();
    this.displayEvergreenEvents();
  }

  setItemsPage() {
    const visibleItems = this.allItems.filter(filterItem => filterItem.visible);

    const maxItem = this.pageCurrent * this.perPage;
    const minItem = maxItem - this.perPage;

    visibleItems.forEach((filterItem, index) => {
      filterItem.html.classList.remove('last');
      filterItem.html.classList.remove('out-of-page');
      if (index >= minItem && index < maxItem) {
        // Visible
      } else {
        filterItem.html.classList.add('out-of-page');
      }
    });

    const inPage = this.allItems.filter(
      filterItem => filterItem.visible && !filterItem.html.classList.contains('out-of-page'),
    );

    if (inPage.length) {
      inPage[inPage.length - 1].html.classList.add('last');
    }
  }

  displayEvergreenEvents() {
    const evenListNodes = [...document.querySelectorAll('.evergreen-post--even')];
    const oddListNodes = [...document.querySelectorAll('.evergreen-post--odd')];

    // If we do not have Lists on this page.
    if (!evenListNodes.length || !oddListNodes.length) {
      return;
    }

    evenListNodes.forEach(node => {
      node.classList.add('out-of-page');
    });

    oddListNodes.forEach(node => {
      node.classList.add('out-of-page');
    });

    // If we have no results.
    const visibleItems = this.allItems.filter(filterItem => filterItem.visible);
    if (!visibleItems.length) {
      return;
    }

    if (this.chosenFilters.length) {
      let anyEvenVisible = false;
      let anyOddVisible = false;

      evenListNodes.forEach(node => {
        node.classList.add('out-of-page');

        const tagObjects = [...node.querySelectorAll(this.tagSelector)];
        const tags = tagObjects.map(tag => tag.innerHTML.replace('*', '').trim());

        let visible = false;

        this.chosenFilters.forEach(filter => {
          if (tags.includes(filter)) {
            visible = true;
          }
        });

        if (anyEvenVisible === false && visible) {
          anyEvenVisible = true;
          node.classList.remove('out-of-page');
        }
      });

      oddListNodes.forEach(node => {
        const tagObjects = [...node.querySelectorAll(this.tagSelector)];
        const tags = tagObjects.map(tag => tag.innerHTML.replace('*', '').trim());

        let visible = false;

        this.chosenFilters.forEach(filter => {
          if (tags.includes(filter)) {
            visible = true;
          }
        });

        if (anyOddVisible === false && visible) {
          anyOddVisible = true;
          node.classList.remove('out-of-page');
        }
      });
    } else {
      evenListNodes.forEach((node, index) => {
        if (index + 1 === this.pageCurrent) {
          node.classList.remove('out-of-page');
        }
      });

      oddListNodes.forEach((node, index) => {
        if (index + 1 === this.pageCurrent) {
          node.classList.remove('out-of-page');
        }
      });
    }
  }

  startPagination() {
    if (this.pageCurrent === 1) {
      this.pageCode += `<li class="pagination__next-prev previousPage js--paginate-prev disabled">
          <button class="pagination__next-prev-button disabled">
            <svg class="pagination__icon">
              <use xlink:href="#previous-icon"></use>
            </svg>
            <span class="visually-hidden">Previous</span>
          </button>
        </li>`;
    } else {
      this.pageCode += `<li class="pagination__next-prev previousPage js--paginate-prev">
          <button class="pagination__next-prev-button">
            <svg class="pagination__icon">
              <use xlink:href="#previous-icon"></use>
            </svg>
            <span class="visually-hidden">Previous</span>
          </button>
        </li>`;
    }

    if (this.pageCount < this.pageStep * 2 + 6) {
      this.addPages(1, this.pageCount + 1);
    } else if (this.pageCurrent < this.pageStep * 2 + 1) {
      this.addPages(1, this.pageStep * 2 + 4);
      this.addLastPage();
    } else if (this.pageCurrent > this.pageCount - this.pageStep * 2) {
      this.addFirstPage();
      this.addPages(this.pageCount - this.pageStep * 2 - 2, this.pageCount + 1);
    } else {
      this.addFirstPage();
      this.addPages(this.pageCurrent - this.pageStep, this.pageCurrent + this.pageStep + 1);
      this.addLastPage();
    }

    if (this.pageCurrent === this.pageCount) {
      this.pageCode += `<li class="pagination__next-prev nextPage js--paginate-next disabled">
          <button class="pagination__next-prev-button disabled">
            <svg class="pagination__icon">
              <use xlink:href="#next-icon"></use>
            </svg>
            <span class="visually-hidden">Next</span>
          </button>
        </li>`;
    } else {
      this.pageCode += `<li class="pagination__next-prev nextPage js--paginate-next">
          <button class="pagination__next-prev-button">
            <svg class="pagination__icon">
              <use xlink:href="#next-icon"></use>
            </svg>
            <span class="visually-hidden">Next</span>
          </button>
        </li>`;
    }

    const elm = document.getElementsByClassName('js--paginate')[0];
    elm.getElementsByTagName('ul')[0].innerHTML = this.pageCode;
    this.pageCode = '';
    this.bindPages();
  }

  prevPage() {
    this.pageCurrent--;
    if (this.pageCurrent < 1) {
      this.pageCurrent = 1;
    }

    this.toggleVisible();
    // this.startPagination();
  }

  nextPage() {
    this.pageCurrent++;
    if (this.pageCurrent > this.pageCount) {
      this.pageCurrent = this.pageCount;
    }
    this.toggleVisible();
    // this.startPagination();
  }

  clickPage(e) {
    const newPage = e.target.getAttribute('data-page');
    this.pageCurrent = parseInt(newPage);
    this.toggleVisible();
    // this.startPagination();
  }

  // Add pages by number (from [s] to [f])
  addPages(s, f) {
    for (let i = s; i < f; i++) {
      const newPage = `<li class="pagination__list-item" data-page="${i}"><a class="pagination__link" data-page="${i}">${i}</a></li>`;
      this.pageCode += newPage;
    }
  }

  addLastPage() {
    const divider = `<li class="pagination__list-divider">...</li>`;
    const newPage = `<li class="pagination__list-item" data-page="${this.pageCount}"><a class="pagination__link" data-page="${this.pageCount}">${this.pageCount}</a></li>`;
    this.pageCode += `${divider}${newPage}`;
  }

  addFirstPage() {
    const newPage = `<li class="pagination__list-item" data-page="1"><a class="pagination__link" data-page="1">1</a></li>`;
    const divider = `<li class="pagination__list-divider">...</li>`;
    this.pageCode += `${newPage}${divider}`;
  }

  bindPages() {
    const elm = document.getElementsByClassName('js--paginate')[0];
    const pages = elm.getElementsByTagName('li');

    for (let i = 0; i < pages.length; i++) {
      if (pages[i].classList.contains('js--paginate-next')) {
        pages[i].addEventListener('click', this.nextPage.bind(this), false);
      } else if (pages[i].classList.contains('js--paginate-prev')) {
        pages[i].addEventListener('click', this.prevPage.bind(this), false);
      } else {
        if (parseInt(pages[i].getAttribute('data-page')) === this.pageCurrent) {
          pages[i].classList.add('selected');
        }
        pages[i].addEventListener('click', this.clickPage.bind(this), false);
      }
    }
  }
}

export default Filters;
